<script setup lang="ts">
import { useProductNoun } from "@/composition/useProductNoun";
import { useAppStore } from "@/store/app";
import {
  buildDefaultStateHtml,
  DEFAULT_STATE_EMOJI,
  getBrowserTimezone,
  type FormSchedule,
  type ScheduleAppearance,
  type ScheduleBoundary,
  type VisibilityState,
  type VisibilityStateKey,
} from "@/util/formSchedule";
import { UISwitch } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import ScheduleAppearancePanel from "./ScheduleAppearance.vue";
import ScheduleTimeline from "./ScheduleTimeline.vue";
import ScheduleWindowFields from "./ScheduleWindowFields.vue";
import VisibilityStateCards from "./VisibilityStateCards.vue";

const { t } = useI18n();
const appStore = useAppStore();
const { productParams } = useProductNoun();

const schedule = computed<FormSchedule>(
  () => appStore.builderConfig.formSchedule
);

/** Replace the schedule immutably and mark the change for save + undo history. */
const commit = (next: FormSchedule) => {
  appStore.builderConfig.formSchedule = next;
  appStore.anyUnsavedChanges = true;
  appStore.addToHistory();
};

/** Seed a state's page with the default message only when it's still empty. */
const seedState = (
  key: VisibilityStateKey,
  state: VisibilityState
): VisibilityState => {
  if (state.html) {
    return state;
  }
  const title =
    key === "before"
      ? t("form.scheduleVisibility.defaultBeforeMessage", productParams.value)
      : t("form.scheduleVisibility.defaultAfterMessage", productParams.value);
  const body =
    key === "before"
      ? t("form.scheduleVisibility.defaultBeforeBody", productParams.value)
      : t("form.scheduleVisibility.defaultAfterBody", productParams.value);
  return {
    ...state,
    html: buildDefaultStateHtml(DEFAULT_STATE_EMOJI[key], title, body),
  };
};

const setEnabled = (enabled: boolean) => {
  const current = schedule.value;
  commit({
    ...current,
    enabled,
    timezone:
      enabled && !current.timezone ? getBrowserTimezone() : current.timezone,
    states: enabled
      ? {
          before: seedState("before", current.states.before),
          after: seedState("after", current.states.after),
        }
      : current.states,
  });
};

const updateOpen = (open: ScheduleBoundary) => {
  commit({ ...schedule.value, open });
};

const updateClose = (close: ScheduleBoundary) => {
  commit({ ...schedule.value, close });
};

const updateTimezone = (timezone: string) => {
  commit({ ...schedule.value, timezone });
};

const updateState = (key: VisibilityStateKey, value: VisibilityState) => {
  commit({
    ...schedule.value,
    states: { ...schedule.value.states, [key]: value },
  });
};

const updateAppearance = (appearance: ScheduleAppearance) => {
  commit({ ...schedule.value, appearance });
};
</script>

<template>
  <div class="schedule-visibility">
    <div class="schedule-visibility__toggle">
      <UISwitch
        id="settings-switch-schedule-visibility"
        :value="schedule.enabled"
        class="mt-1 flex-shrink-0"
        @update:value="setEnabled"
      />
      <div>
        <p class="font-medium text-gray-700">
          {{ t("form.scheduleVisibility.title", productParams) }}
        </p>
        <p class="mt-1 text-sm text-gray-500">
          {{ t("form.scheduleVisibility.subtitle", productParams) }}
        </p>
      </div>
    </div>

    <div v-if="schedule.enabled" class="schedule-visibility__body">
      <ScheduleTimeline :schedule="schedule" />
      <ScheduleWindowFields
        :open="schedule.open"
        :close="schedule.close"
        :timezone="schedule.timezone"
        @update:open="updateOpen"
        @update:close="updateClose"
        @update:timezone="updateTimezone"
      />
      <VisibilityStateCards
        :states="schedule.states"
        :appearance="schedule.appearance"
        @update:state="updateState"
      />
      <ScheduleAppearancePanel
        :appearance="schedule.appearance"
        @update:appearance="updateAppearance"
      />
    </div>
  </div>
</template>

<style scoped>
.schedule-visibility {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.schedule-visibility__toggle {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

/* Match the other Form Settings rows: no default paragraph bottom-margin
   (SettingsView's scoped reset doesn't reach into this child component). */
.schedule-visibility__toggle p {
  margin-bottom: 0;
}

.schedule-visibility__body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-left: 52px;
}
</style>
